import React, {useEffect} from 'react';
import {PageWrapper} from "../../../../components/UI/PageWrapper/PageWrapper";
import styled from "styled-components";
import {Alert} from "antd";
import {useIntl, defineMessages} from "react-intl";
import {mediaScreenSizes} from "../../../../utils/constans/styles/screenSizes";
import {SubscriptionHeader} from "./components/SubscriptionHeader";
import {GracePeriodBanner} from "./components/GracePeriodBanner";
import {BlockedAccessScreen} from "./components/BlockedAccessScreen";
import {PaymentMethodModal} from "./components/PaymentMethodModal";
import {UsageStats} from "./components/UsageStats";
import {AdjustSeatsSection} from "./components/AdjustSeatsSection";
import {InvoiceHistory} from "./components/InvoiceHistory";
import {PaymentMethodsList} from "./components/PaymentMethodsList";
import {useAppDispatch, useAppSelector} from "../../../../store/store";
import {getTariff, clearTariff, getSubscriptionPrices, clearSubscriptionPrices} from "../../../../store/reducers/tariffReducer/tariffReducer";
import {getManagerList, clearManagerList} from "../../../../store/reducers/personnelManagementReducer/personnelManagementReducer";
import {getPlaneList, clearPlaneList} from "../../../../store/reducers/fleetReducer/fleetReducer";
import {groupFreighter, groupCharterer} from "../../../../config/acl/roles/groups";
import {useState} from "react";
import dayjs from "dayjs";

const SubscriptionPageMessages = defineMessages({
  discountPromoBanner: {
    id: "subscriptionPage.discountPromoBanner",
    defaultMessage: "{percent}% off for the first {months} months when you start your subscription!",
  },
});

const SubscriptionContent = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 20px;
  width: 95%;

  @media(max-width: ${mediaScreenSizes.tablet}) {
    width: 100%;
    row-gap: 16px;
  }

  @media(max-width: ${mediaScreenSizes.mobile}) {
    width: 100%;
    row-gap: 16px;
  }
`;

export const SubscriptionPage = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const user = useAppSelector(state => state.user.oneItemObject.item);
  const tariff = useAppSelector(state => state.tariff.oneItemObject.item);
  const isTariffLoading = useAppSelector(state => state.tariff.oneItemObject.isLoading);
  const isPricesLoading = useAppSelector(state => state.tariff.getSubscriptionPrices.isLoading);
  const isPersonnelLoading = useAppSelector(state => state.personnelManagement.itemsObject.isLoading);
  const isFleetLoading = useAppSelector(state => state.fleet.itemsObject.isLoading);
  const personnelTotalCount = useAppSelector(state => state.personnelManagement.itemsObject.totalCount);
  const fleetTotalCount = useAppSelector(state => state.fleet.itemsObject.totalCount);
  const subscriptionPrices = useAppSelector(state => state.tariff.getSubscriptionPrices);
  const [isPaymentMethodModalVisible, setIsPaymentMethodModalVisible] = useState(false);
  
  const isOperatorOrBroker = groupFreighter.includes(user.role) || groupCharterer.includes(user.role);
  const isOperator = groupFreighter.includes(user.role);

  useEffect(() => {
    if (isOperatorOrBroker) {
      dispatch(getTariff({}));
      dispatch(getSubscriptionPrices({}));
      dispatch(getManagerList({ page: 1 }));
      if (isOperator) {
        dispatch(getPlaneList({ query: '', page: 1 }));
      }
    }
    return () => {
      if (isOperatorOrBroker) {
        dispatch(clearTariff());
        dispatch(clearSubscriptionPrices());
        dispatch(clearManagerList());
        if (isOperator) {
          dispatch(clearPlaneList());
        }
      }
    };
  }, [dispatch, isOperatorOrBroker, isOperator]);

  const activePackage = tariff?.companyTariffPackages?.find(pkg => pkg.active);
  const subscriptionStatus = activePackage?.subscriptionStatus;
  const gracePeriodUntil = activePackage?.gracePeriodUntil;
  const discountPercent = subscriptionPrices.discountPercent;
  const discountDurationMonths = subscriptionPrices.discountDurationMonths;
  const hasDiscount = discountPercent != null && discountPercent > 0 && discountDurationMonths != null && discountDurationMonths > 0;
  // Only show banner for first-time subscriptions - never for renewals after cancel
  const neverHadSubscription = !activePackage?.stripeSubscriptionId || subscriptionStatus === 'not_started';
  const showDiscountBanner = hasDiscount && neverHadSubscription;
  // Discount applies to UsageStats when: (a) pre-subscription promo, or (b) current subscription has active discount
  const subscriptionHasActiveDiscount =
    (activePackage?.discountPercent ?? 0) > 0 &&
    activePackage?.discountEndsAt != null &&
    dayjs(activePackage.discountEndsAt).isAfter(dayjs());
  const discountAppliesForUsageStats = showDiscountBanner || subscriptionHasActiveDiscount;

  // Check if account is blocked (past_due and grace period ended)
  const isBlocked = !isTariffLoading && tariff && subscriptionStatus === 'past_due' && gracePeriodUntil && dayjs(gracePeriodUntil).isBefore(dayjs());
  
  // Check if in grace period (past_due but grace period not ended)
  const isInGracePeriod = subscriptionStatus === 'past_due' && gracePeriodUntil && dayjs(gracePeriodUntil).isAfter(dayjs());

  const isHeaderLoading = isTariffLoading || isPricesLoading;
  const isUsageStatsLoading = isPricesLoading || isPersonnelLoading || (isOperator && isFleetLoading);

  const handleUpdatePaymentMethod = () => {
    setIsPaymentMethodModalVisible(true);
  };

  const handlePaymentMethodSuccess = () => {
    // Tariff is refreshed by attachPaymentMethod saga
    setIsPaymentMethodModalVisible(false);
  };

  // Show blocked screen if account is blocked
  if (isBlocked) {
    return (
      <PageWrapper>
        <SubscriptionContent>
          <BlockedAccessScreen
            onUpdatePaymentMethod={handleUpdatePaymentMethod}
          />
          <PaymentMethodModal
            isVisible={isPaymentMethodModalVisible}
            setIsVisible={setIsPaymentMethodModalVisible}
            onSuccess={handlePaymentMethodSuccess}
          />
        </SubscriptionContent>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <SubscriptionContent>
        {isInGracePeriod && activePackage && (
          <GracePeriodBanner
            tariffPackage={activePackage}
            onUpdatePaymentMethod={handleUpdatePaymentMethod}
          />
        )}
        {showDiscountBanner && (
          <Alert
            message={intl.formatMessage(SubscriptionPageMessages.discountPromoBanner, {
              percent: discountPercent,
              months: discountDurationMonths,
            })}
            type="success"
            showIcon
          />
        )}
        <SubscriptionHeader
          tariff={tariff ?? undefined}
          subscriptionPrices={subscriptionPrices}
          activeUsers={activePackage?.billedUsersCount ?? (isOperator ? personnelTotalCount : (personnelTotalCount ?? 0) + 1)}
          activeAircraft={activePackage?.billedAircraftCount ?? (isOperator ? fleetTotalCount : undefined)}
          isOperator={isOperator}
          isLoading={isHeaderLoading}
        />
        <UsageStats
          activeUsers={activePackage?.billedUsersCount ?? (isOperator ? personnelTotalCount : (personnelTotalCount ?? 0) + 1)}
          activeAircraft={activePackage?.billedAircraftCount ?? (isOperator ? fleetTotalCount : undefined)}
          subscribedUsersCount={activePackage?.subscribedUsersCount}
          subscribedAircraftCount={activePackage?.subscribedAircraftCount}
          isOperator={isOperator}
          pricePerSeatCents={subscriptionPrices.pricePerSeatCents}
          pricePerAircraftCents={subscriptionPrices.pricePerAircraftCents}
          currency={subscriptionPrices.currency}
          discountPercent={subscriptionHasActiveDiscount ? (activePackage?.discountPercent ?? subscriptionPrices.discountPercent) : subscriptionPrices.discountPercent}
          discountDurationMonths={subscriptionPrices.discountDurationMonths}
          discountEndsAt={activePackage?.discountEndsAt ?? subscriptionPrices.discountEndsAt}
          discountApplies={discountAppliesForUsageStats}
          isLoading={isUsageStatsLoading}
        />
        <AdjustSeatsSection
          activePackage={activePackage ?? null}
          isOperator={isOperator}
          pricePerSeatCents={subscriptionPrices.pricePerSeatCents}
          pricePerAircraftCents={subscriptionPrices.pricePerAircraftCents}
          currency={subscriptionPrices.currency}
          discountPercent={subscriptionHasActiveDiscount ? (activePackage?.discountPercent ?? subscriptionPrices.discountPercent) : subscriptionPrices.discountPercent}
          discountApplies={discountAppliesForUsageStats}
        />
        <PaymentMethodsList />
        <InvoiceHistory />
        <PaymentMethodModal
          isVisible={isPaymentMethodModalVisible}
          setIsVisible={setIsPaymentMethodModalVisible}
          onSuccess={handlePaymentMethodSuccess}
        />
      </SubscriptionContent>
    </PageWrapper>
  );
};





